import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Trash2, Image as ImageIcon, Plus, UploadCloud, Loader2, Paperclip, Inbox, Settings, Flame, Boxes, Star, Check, Pencil, MessageSquare, MessageCircle, Box, Camera, Gift, Users, Euro, Archive, RotateCcw, XCircle, Wallet, Coins, BarChart3, Folder, FolderPlus, FolderInput, Percent, Copy, Search } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
const MONTHS_IT = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];
const fmtMonth = (ym) => { const [y, m] = ym.split("-"); return `${MONTHS_IT[parseInt(m, 10) - 1]} ${y.slice(2)}`; };
import { Navbar } from "../components/Navbar";
import { StatusBadge } from "../components/StatusBadge";
import { SettingsEditor } from "../components/SettingsEditor";
import { NotificationBell } from "../components/NotificationBell";
import { PushToggle } from "../components/PushToggle";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { api, fileUrl, STATUSES, STATUS_COLORS, SERVICES } from "../lib/api";
import { StlViewer } from "../components/StlViewer";
import { QRCodeCanvas } from "qrcode.react";

function RequestCard({ r, i, prefix = "", onChangeStatus, onDelete, onReload }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
      data-testid={`${prefix}request-card-${i}`}
      className="p-5 bg-card border border-white/10 rounded-sm hover:border-neon-cyan/40 transition-colors duration-200">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h3 className="font-display font-bold text-lg tracking-tight text-white">{r.title}</h3>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm border border-white/15 text-muted-foreground">
              {r.service_type === "Incisione Laser" ? <Flame className="w-3 h-3" /> : <Boxes className="w-3 h-3" />}
              {r.service_type || "Stampa 3D"}
            </span>
            <StatusBadge status={r.status} testId={`${prefix}request-status-${i}`} />
            {r.loyalty_eligible && (
              <span data-testid={`${prefix}loyalty-badge-${i}`} className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm bg-neon-green/15 text-neon-green border border-neon-green/40" title={`${r.client_order_count} ordini`}>
                <Gift className="w-3 h-3" /> Sconto 10% attivo
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground font-light mt-2">{r.description}</p>
          <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-xs font-mono text-muted-foreground">
            {r.order_code && <span data-testid={`${prefix}request-code-${i}`} style={{ color: "#FFE600" }}>#{r.order_code}</span>}
            <span className="text-neon-cyan">{r.client_name}</span>
            {r.client_email && <span>{r.client_email}</span>}
            {r.client_phone && <span>{r.client_phone}</span>}
            {r.material && <span>Materiale: {r.material}</span>}
            <span>Qtà: {r.quantity}</span>
            {r.expected_delivery && (() => {
              const d = new Date(r.expected_delivery); const today = new Date(); today.setHours(0, 0, 0, 0);
              const days = Math.ceil((d - today) / 86400000);
              const done = r.status === "Consegnato" || r.status === "Completato";
              const overdue = !done && days < 0;
              const urgent = !done && days >= 0 && days <= 3;
              return (
                <span data-testid={`${prefix}request-delivery-${i}`} className="flex items-center gap-1.5" style={{ color: overdue ? "#FF3B3B" : urgent ? "#FF9500" : "#FFE600" }}>
                  Consegna prevista: {d.toLocaleDateString("it-IT")}
                  {overdue ? <span data-testid={`${prefix}request-overdue-${i}`} className="px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase" style={{ background: "rgba(255,59,59,0.15)", border: "1px solid rgba(255,59,59,0.5)" }}>Scaduto</span>
                    : urgent ? <span data-testid={`${prefix}request-urgent-${i}`} className="px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase" style={{ background: "rgba(255,149,0,0.15)", border: "1px solid rgba(255,149,0,0.5)" }}>In scadenza</span> : null}
                </span>
              );
            })()}
            {r.attachment_path && (
              <a href={fileUrl(r.attachment_path)} target="_blank" rel="noreferrer" data-testid={`${prefix}request-attachment-${i}`}
                 className="flex items-center gap-1 text-neon-pink hover:underline"><Paperclip className="w-3 h-3" /> Allegato</a>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {r.client_phone && (
              <WhatsAppDialog r={r} index={i} onChanged={onReload} />
            )}
            {r.attachment_path && r.attachment_path.toLowerCase().endsWith(".stl") && (
              <StlDialog url={fileUrl(r.attachment_path)} index={i} />
            )}
            {r.order_code && (
              <span data-testid={`${prefix}request-qr-${i}`} title="QR stato ordine" className="inline-flex bg-[#0D0E15] p-1 rounded-sm border border-white/10">
                <QRCodeCanvas value={`${window.location.origin}/stato?code=${r.order_code}`} size={40} bgColor="#0D0E15" fgColor="#00F0FF" />
              </span>
            )}
          </div>
          <ProgressPhotos req={r} onChanged={onReload} index={i} />
        </div>
        <div className="flex items-end gap-2">
          <div className="flex flex-col">
            <label className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground mb-0.5">Incasso €</label>
            <Input data-testid={`${prefix}request-price-${i}`} type="number" min="0" step="0.01" placeholder="0.00"
              defaultValue={r.price ?? ""}
              onBlur={async (e) => { if (e.target.value !== "") { await api.put(`/requests/${r.id}`, { price: Number(e.target.value) }); onReload(); } }}
              className="w-24 bg-input border-white/10 text-white rounded-sm text-xs" />
          </div>
          <button data-testid={`${prefix}request-paid-${i}`} onClick={async () => { await api.put(`/requests/${r.id}`, { paid: !r.paid }); onReload(); }}
            className={`flex items-center gap-1.5 px-2.5 h-9 self-end rounded-sm border text-xs font-semibold transition-colors duration-200 ${r.paid ? "bg-neon-green/15 border-neon-green/50 text-neon-green" : "bg-input border-white/10 text-muted-foreground hover:border-white/30"}`}>
            {r.paid ? <Check className="w-3.5 h-3.5" /> : <span className="w-3.5 h-3.5 rounded-sm border border-current inline-block" />} {r.paid ? "Pagato" : "Da pagare"}
          </button>
          <div className="flex flex-col">
            <label className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground mb-0.5" title="Unità fedeltà: es. 2 se l'ordine ha 2 prodotti">Fedeltà</label>
            <Input data-testid={`${prefix}request-loyalty-${i}`} type="number" min="1" step="1"
              defaultValue={r.loyalty_units ?? 1}
              onBlur={async (e) => { const v = parseInt(e.target.value, 10); if (v >= 1) { await api.put(`/requests/${r.id}`, { loyalty_units: v }); onReload(); } }}
              className="w-16 bg-input border-white/10 text-white rounded-sm text-xs self-end" />
          </div>
          <Select value={r.status} onValueChange={(v) => onChangeStatus(r.id, v)}>
            <SelectTrigger data-testid={`${prefix}request-status-select-${i}`} className="w-40 bg-input border-white/10 text-white rounded-sm text-xs"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-card border-white/10 text-white">
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button data-testid={`${prefix}request-delete-${i}`} onClick={() => onDelete(r.id)} variant="ghost" size="icon" className="text-muted-foreground hover:text-neon-pink hover:bg-neon-pink/10">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </motion.div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState({});
  const [requests, setRequests] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [clients, setClients] = useState([]);
  const [trash, setTrash] = useState({ requests: [], gallery: [], feedback: [] });
  const [earnings, setEarnings] = useState({ total_revenue: 0, total_pending: 0, total_orders: 0, clients: [], orders: [] });
  const [folders, setFolders] = useState([]);
  const [selected, setSelected] = useState([]);
  const [moveOpen, setMoveOpen] = useState(false);
  const [filter, setFilter] = useState("Tutti");
  const [order, setOrder] = useState("desc");
  const [archiveQuery, setArchiveQuery] = useState("");

  const load = useCallback(async () => {
    try {
      const [s, r, g, f, c, t, ea, fld] = await Promise.all([
        api.get("/stats"),
        api.get("/requests", { params: { ...(filter !== "Tutti" ? { status: filter } : {}), order } }),
        api.get("/gallery"),
        api.get("/feedback/all"),
        api.get("/clients"),
        api.get("/trash"),
        api.get("/earnings"),
        api.get("/gallery/folders"),
      ]);
      setStats(s.data);
      setRequests(r.data);
      setGallery(g.data);
      setFolders(fld.data);
      setFeedback(f.data);
      setClients(c.data);
      setTrash(t.data);
      setEarnings(ea.data);
    } catch (e) {
      toast.error("Errore nel caricamento dei dati della dashboard");
    }
  }, [filter, order]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, status) => {
    await api.put(`/requests/${id}`, { status });
    toast.success("Stato aggiornato");
    load();
  };
  const delRequest = async (id) => {
    await api.delete(`/requests/${id}`);
    toast.success("Richiesta eliminata");
    load();
  };
  const delGallery = async (id) => {
    await api.delete(`/gallery/${id}`);
    toast.success("Foto rimossa");
    load();
  };
  const delFolder = async (fid) => {
    if (!window.confirm("Eliminare la cartella? Le foto verranno spostate in 'Senza cartella'.")) return;
    try { await api.delete(`/gallery/folders/${fid}`); toast.success("Cartella eliminata"); load(); }
    catch { toast.error("Errore eliminazione cartella"); }
  };
  const toggleSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const quickMove = (id) => { setSelected([id]); setMoveOpen(true); };
  const approveFeedback = async (id) => { await api.put(`/feedback/${id}/approve`); toast.success("Recensione pubblicata"); load(); };
  const delFeedback = async (id) => { await api.delete(`/feedback/${id}`); toast.success("Recensione eliminata"); load(); };
  const restoreItem = async (kind, id) => { try { await api.post(`/trash/${kind}/${id}/restore`); toast.success("Elemento ripristinato"); load(); } catch { toast.error("Errore nel ripristino"); } };
  const purgeItem = async (kind, id) => { if (!window.confirm("Eliminare definitivamente? L'operazione non è reversibile.")) return; try { await api.delete(`/trash/${kind}/${id}`); toast.success("Eliminato definitivamente"); load(); } catch { toast.error("Errore nell'eliminazione"); } };
  const trashCount = (trash.requests?.length || 0) + (trash.gallery?.length || 0) + (trash.feedback?.length || 0);
  const cards = [
    { label: "Totale", value: stats.total || 0, color: "#F8F9FA" },
    ...STATUSES.map((s) => ({ label: s, value: stats[s] || 0, color: STATUS_COLORS[s] })),
  ];
  const STATUS_RANK = { "In lavorazione": 0, "In stampa": 1, "In colorazione": 2, "In attesa": 3, "Completato": 4, "Consegnato": 5 };
  const sortedRequests = [...requests].sort((a, b) => (STATUS_RANK[a.status] ?? 3) - (STATUS_RANK[b.status] ?? 3));
  const DONE_STATUSES = ["Completato", "Consegnato"];
  const doneAt = (r) => {
    if (!DONE_STATUSES.includes(r.status)) return null;
    const hist = Array.isArray(r.status_history) ? r.status_history : [];
    const entry = [...hist].reverse().find((h) => DONE_STATUSES.includes(h.status));
    return entry?.at || r.updated_at || r.created_at;
  };
  const isArchived = (r) => {
    if (r.status !== "Consegnato") return false;
    const dt = doneAt(r);
    return dt ? (Date.now() - new Date(dt).getTime()) >= 14 * 86400000 : false;
  };
  const deliveryDays = (r) => {
    if (!r.expected_delivery) return null;
    const d = new Date(r.expected_delivery); const today = new Date(); today.setHours(0, 0, 0, 0);
    return Math.ceil((d - today) / 86400000);
  };
  const urgencyRank = (r) => {
    const days = deliveryDays(r);
    if (days === null) return 2;
    if (days < 0) return 0;   // Scaduto
    if (days <= 3) return 1;  // In scadenza
    return 2;
  };
  const activeRequests = sortedRequests
    .filter((r) => !DONE_STATUSES.includes(r.status))
    .sort((a, b) => urgencyRank(a) - urgencyRank(b));
  const completedRecent = sortedRequests.filter((r) => DONE_STATUSES.includes(r.status) && !isArchived(r));
  const archivedRequests = sortedRequests.filter((r) => isArchived(r));
  const archiveFiltered = archivedRequests.filter((r) => {
    const q = archiveQuery.trim().toLowerCase();
    if (!q) return true;
    return (r.order_code || "").toLowerCase().includes(q) || (r.client_name || "").toLowerCase().includes(q);
  });

  return (
    <div className="noise-overlay min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <span className="font-mono text-xs uppercase tracking-widest text-neon-green">Pannello di controllo</span>
            <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter text-white mt-1">Dashboard</h1>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/statistiche" data-testid="open-stats" className="inline-flex items-center gap-2 h-10 px-3 rounded-sm border border-white/15 text-white hover:border-neon-cyan hover:text-neon-cyan transition-colors duration-200 text-sm font-semibold">
              <BarChart3 className="w-4 h-4" /> Statistiche
            </Link>
            <PushToggle />
            <NotificationBell />
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
          {cards.map((c, i) => (
            <motion.div key={c.label} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
              data-testid={`stat-${i}`}
              className="p-5 bg-card border border-white/10 rounded-sm relative overflow-hidden">
              <div className="font-display font-black text-3xl" style={{ color: c.color }}>{c.value}</div>
              <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground mt-1">{c.label}</div>
              <div className="absolute bottom-0 left-0 h-0.5 w-full" style={{ backgroundColor: c.color, boxShadow: `0 0 10px ${c.color}` }} />
            </motion.div>
          ))}
        </div>

        <Tabs defaultValue="requests">
          <TabsList className="bg-card border border-white/10 rounded-sm flex flex-wrap h-auto gap-1">
            <TabsTrigger value="requests" data-testid="tab-requests" className="data-[state=active]:bg-neon-cyan data-[state=active]:text-black rounded-sm">Richieste</TabsTrigger>
            <TabsTrigger value="gallery" data-testid="tab-gallery" className="data-[state=active]:bg-neon-pink data-[state=active]:text-white rounded-sm">Galleria</TabsTrigger>
            <TabsTrigger value="feedback" data-testid="tab-feedback" className="data-[state=active]:bg-neon-yellow data-[state=active]:text-black rounded-sm gap-1" style={{ color: undefined }}><MessageSquare className="w-3.5 h-3.5" /> Recensioni</TabsTrigger>
            <TabsTrigger value="clients" data-testid="tab-clients" className="data-[state=active]:bg-neon-cyan data-[state=active]:text-black rounded-sm gap-1"><Users className="w-3.5 h-3.5" /> Clienti</TabsTrigger>
            <TabsTrigger value="earnings" data-testid="tab-earnings" className="data-[state=active]:bg-neon-yellow data-[state=active]:text-black rounded-sm gap-1"><Wallet className="w-3.5 h-3.5" /> Guadagni</TabsTrigger>
            <TabsTrigger value="trash" data-testid="tab-trash" className="data-[state=active]:bg-neon-pink data-[state=active]:text-white rounded-sm gap-1"><Archive className="w-3.5 h-3.5" /> Cestino{trashCount > 0 && <span data-testid="trash-count-badge" className="ml-1 min-w-[16px] h-4 px-1 inline-flex items-center justify-center text-[10px] font-bold rounded-full bg-neon-pink text-white">{trashCount}</span>}</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings" className="data-[state=active]:bg-neon-green data-[state=active]:text-black rounded-sm gap-1"><Settings className="w-3.5 h-3.5" /> Impostazioni</TabsTrigger>
            <TabsTrigger value="archive" data-testid="tab-archive" className="data-[state=active]:bg-white data-[state=active]:text-black rounded-sm gap-1"><Archive className="w-3.5 h-3.5" /> Archivio{archivedRequests.length > 0 && <span data-testid="archive-count-badge" className="ml-1 min-w-[16px] h-4 px-1 inline-flex items-center justify-center text-[10px] font-bold rounded-full bg-white text-black">{archivedRequests.length}</span>}</TabsTrigger>
          </TabsList>

          {/* REQUESTS */}
          <TabsContent value="requests" className="mt-6">
            <div className="flex items-center justify-between flex-wrap gap-4 mb-5">
              <div className="w-52">
                <Select value={filter} onValueChange={setFilter}>
                  <SelectTrigger data-testid="filter-status" className="bg-input border-white/10 text-white rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card border-white/10 text-white">
                    <SelectItem value="Tutti">Tutti gli stati</SelectItem>
                    {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-56">
                <Select value={order} onValueChange={setOrder}>
                  <SelectTrigger data-testid="filter-order" className="bg-input border-white/10 text-white rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card border-white/10 text-white">
                    <SelectItem value="asc">Data: crescente</SelectItem>
                    <SelectItem value="desc">Data: decrescente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <NewRequestDialog onCreated={load} />
            </div>

            {requests.length === 0 ? (
              <div data-testid="requests-empty" className="flex flex-col items-center py-20 text-muted-foreground">
                <Inbox className="w-12 h-12 mb-3 opacity-40" /><p className="font-light">Nessuna richiesta.</p>
              </div>
            ) : (
              <div className="space-y-10">
                <section>
                  <h3 className="font-display font-bold text-sm uppercase tracking-widest text-neon-cyan mb-4 flex items-center gap-2">
                    In corso <span className="text-[11px] font-mono text-muted-foreground">({activeRequests.length})</span>
                  </h3>
                  {activeRequests.length === 0 ? (
                    <p data-testid="active-empty" className="text-sm text-muted-foreground font-light">Nessuna lavorazione in corso.</p>
                  ) : (
                    <div className="space-y-4">
                      {activeRequests.map((r, i) => (
                        <RequestCard key={r.id} r={r} i={i} onChangeStatus={changeStatus} onDelete={delRequest} onReload={load} />
                      ))}
                    </div>
                  )}
                </section>
                <section>
                  <h3 className="font-display font-bold text-sm uppercase tracking-widest text-neon-green mb-1 flex items-center gap-2">
                    Completate / Consegnate <span className="text-[11px] font-mono text-muted-foreground">({completedRecent.length})</span>
                  </h3>
                  <p className="text-xs text-muted-foreground font-light mb-4">I "Consegnato" passano in Archivio dopo 2 settimane. I "Completato" restano sempre qui.</p>
                  {completedRecent.length === 0 ? (
                    <p data-testid="completed-empty" className="text-sm text-muted-foreground font-light">Nessuna lavorazione completata di recente.</p>
                  ) : (
                    <div className="space-y-4">
                      {completedRecent.map((r, i) => (
                        <RequestCard key={r.id} r={r} i={activeRequests.length + i} onChangeStatus={changeStatus} onDelete={delRequest} onReload={load} />
                      ))}
                    </div>
                  )}
                </section>
              </div>
            )}
          </TabsContent>

          {/* ARCHIVE */}
          <TabsContent value="archive" className="mt-6">
            <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
              <div>
                <h2 className="font-display font-black text-xl text-white">Archivio</h2>
                <p className="text-sm text-muted-foreground font-light mt-1">Ordini consegnati da più di 2 settimane.</p>
              </div>
              <div className="w-full sm:w-72">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  <Input data-testid="archive-search" value={archiveQuery} onChange={(e) => setArchiveQuery(e.target.value)}
                    placeholder="Cerca per codice o nome cliente…"
                    className="pl-9 bg-input border-white/10 text-white rounded-sm text-sm" />
                </div>
              </div>
            </div>
            {archivedRequests.length === 0 ? (
              <div data-testid="archive-empty" className="flex flex-col items-center py-20 text-muted-foreground">
                <Archive className="w-12 h-12 mb-3 opacity-40" /><p className="font-light">Nessuna lavorazione in archivio.</p>
              </div>
            ) : archiveFiltered.length === 0 ? (
              <div data-testid="archive-no-results" className="flex flex-col items-center py-20 text-muted-foreground">
                <Search className="w-12 h-12 mb-3 opacity-40" /><p className="font-light">Nessun risultato per "{archiveQuery}".</p>
              </div>
            ) : (
              <div className="space-y-4">
                {archiveFiltered.map((r, i) => (
                  <RequestCard key={r.id} r={r} i={i} prefix="archive-" onChangeStatus={changeStatus} onDelete={delRequest} onReload={load} />
                ))}
              </div>
            )}
          </TabsContent>

          {/* GALLERY */}
          <TabsContent value="gallery" className="mt-6">
            <div className="flex flex-wrap items-center gap-3 mb-5">
              {selected.length > 0 && (
                <>
                  <span data-testid="gallery-selected-count" className="text-sm font-mono text-neon-cyan">{selected.length} selezionate</span>
                  <Button data-testid="gallery-move-open" onClick={() => setMoveOpen(true)} className="bg-neon-cyan text-black hover:bg-white rounded-sm font-semibold gap-2"><FolderInput className="w-4 h-4" /> Sposta ({selected.length})</Button>
                  <Button data-testid="gallery-selection-cancel" variant="outline" onClick={() => setSelected([])} className="border-white/15 text-white hover:bg-white/5 rounded-sm">Annulla</Button>
                </>
              )}
              <div className="ml-auto flex gap-3">
                <NewFolderDialog onCreated={load} />
                <NewGalleryDialog folders={folders} onCreated={load} />
              </div>
            </div>
            {gallery.length === 0 ? (
              <div data-testid="admin-gallery-empty" className="flex flex-col items-center py-20 text-muted-foreground">
                <ImageIcon className="w-12 h-12 mb-3 opacity-40" /><p className="font-light">Nessuna foto in galleria.</p>
              </div>
            ) : (() => {
              const fmap = Object.fromEntries(folders.map((f) => [f.id, f]));
              const byFolder = {};
              const uncat = [];
              for (const g of gallery) {
                const fid = g.folder_id || g.group_id;
                if (fid && fmap[fid]) (byFolder[fid] = byFolder[fid] || []).push(g);
                else uncat.push(g);
              }
              const ordered = [...folders.flatMap((f) => byFolder[f.id] || []), ...uncat];
              const idxOf = {};
              ordered.forEach((p, i) => { idxOf[p.id] = i; });
              const renderGrid = (photos) => (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {photos.map((g) => (
                    <PhotoCard key={g.id} g={g} index={idxOf[g.id]} selected={selected.includes(g.id)}
                      onToggle={toggleSelect} onQuickMove={quickMove} onSaved={load} onDelete={delGallery} />
                  ))}
                </div>
              );
              return (
                <div className="space-y-8">
                  {folders.map((f) => (
                    <div key={f.id} data-testid={`admin-folder-${f.id}`}>
                      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-white/10">
                        <Folder className="w-4 h-4 text-neon-cyan" />
                        <h3 className="font-display font-bold text-white">{f.name}</h3>
                        <span className="text-xs font-mono text-muted-foreground">{(byFolder[f.id] || []).length}</span>
                        <div className="ml-auto flex gap-1.5">
                          <RenameFolderDialog folder={f} onSaved={load} />
                          <Button data-testid={`folder-delete-${f.id}`} onClick={() => delFolder(f.id)} size="icon" className="w-7 h-7 bg-black/50 text-neon-pink hover:bg-neon-pink hover:text-white rounded-sm"><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                      </div>
                      {(byFolder[f.id] || []).length === 0 ? (
                        <p className="text-xs text-muted-foreground font-light">Cartella vuota — seleziona delle foto e spostale qui.</p>
                      ) : renderGrid(byFolder[f.id])}
                    </div>
                  ))}
                  {uncat.length > 0 && (
                    <div data-testid="admin-folder-none">
                      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-white/10">
                        <ImageIcon className="w-4 h-4 text-muted-foreground" />
                        <h3 className="font-display font-bold text-white">Senza cartella</h3>
                        <span className="text-xs font-mono text-muted-foreground">{uncat.length}</span>
                      </div>
                      {renderGrid(uncat)}
                    </div>
                  )}
                </div>
              );
            })()}
            <MoveDialog open={moveOpen} onOpenChange={setMoveOpen} photoIds={selected} folders={folders} onMoved={() => { setSelected([]); load(); }} />
          </TabsContent>

          {/* FEEDBACK */}
          <TabsContent value="feedback" className="mt-6">
            {feedback.length === 0 ? (
              <div data-testid="admin-feedback-empty" className="flex flex-col items-center py-20 text-muted-foreground">
                <MessageSquare className="w-12 h-12 mb-3 opacity-40" /><p className="font-light">Nessuna recensione ricevuta.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {feedback.map((f, i) => (
                  <div key={f.id} data-testid={`admin-feedback-${i}`} className="p-5 bg-card border border-white/10 rounded-sm flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <div className="flex">{[1, 2, 3, 4, 5].map((n) => <Star key={n} className="w-4 h-4" style={{ color: n <= f.rating ? "#FFE600" : "#3a3d4d", fill: n <= f.rating ? "#FFE600" : "transparent" }} />)}</div>
                        {f.approved
                          ? <span className="text-[10px] font-mono uppercase tracking-wider text-neon-green border border-neon-green/40 px-2 py-0.5 rounded-sm">Pubblicata</span>
                          : <span className="text-[10px] font-mono uppercase tracking-wider border px-2 py-0.5 rounded-sm" style={{ color: "#FFE600", borderColor: "#FFE60055" }}>In attesa</span>}
                      </div>
                      <p className="text-sm text-foreground font-light mt-2">"{f.message}"</p>
                      <p className="font-mono text-xs text-neon-cyan mt-2">{f.name}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {!f.approved && (
                        <Button data-testid={`feedback-approve-${i}`} onClick={() => approveFeedback(f.id)} size="icon" className="w-9 h-9 bg-neon-green text-black hover:bg-white rounded-sm"><Check className="w-4 h-4" /></Button>
                      )}
                      <Button data-testid={`feedback-del-${i}`} onClick={() => delFeedback(f.id)} variant="ghost" size="icon" className="text-muted-foreground hover:text-neon-pink hover:bg-neon-pink/10"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* EARNINGS */}
          <TabsContent value="earnings" className="mt-6">
            <div className="grid sm:grid-cols-3 gap-4 mb-8">
              <div data-testid="earnings-total" className="p-6 bg-card border border-neon-green/40 rounded-sm relative overflow-hidden">
                <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-muted-foreground"><Coins className="w-4 h-4" style={{ color: "#39FF14" }} /> Già incassato</div>
                <div className="font-display font-black text-4xl mt-2 flex items-center gap-1" style={{ color: "#39FF14" }}><Euro className="w-7 h-7" />{(earnings.total_revenue || 0).toFixed(2)}</div>
                <div className="absolute bottom-0 left-0 h-0.5 w-full" style={{ backgroundColor: "#39FF14", boxShadow: "0 0 10px #39FF14" }} />
              </div>
              <div data-testid="earnings-pending" className="p-6 bg-card border border-neon-yellow/40 rounded-sm relative overflow-hidden">
                <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-muted-foreground"><Wallet className="w-4 h-4" style={{ color: "#FFE600" }} /> Da incassare</div>
                <div className="font-display font-black text-4xl mt-2 flex items-center gap-1" style={{ color: "#FFE600" }}><Euro className="w-7 h-7" />{(earnings.total_pending || 0).toFixed(2)}</div>
                <div className="absolute bottom-0 left-0 h-0.5 w-full" style={{ backgroundColor: "#FFE600", boxShadow: "0 0 10px #FFE600" }} />
              </div>
              <div data-testid="earnings-orders" className="p-6 bg-card border border-white/10 rounded-sm">
                <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-muted-foreground"><Boxes className="w-4 h-4 text-neon-cyan" /> Ordini totali</div>
                <div className="font-display font-black text-4xl mt-2 text-neon-cyan">{earnings.total_orders || 0}</div>
              </div>
            </div>
            <div data-testid="earnings-chart" className="p-6 bg-card border border-white/10 rounded-sm mb-8">
              <h3 className="font-display font-bold text-sm uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-2"><BarChart3 className="w-4 h-4" style={{ color: "#FFE600" }} /> Andamento incassi (ultimi 12 mesi)</h3>
              <div style={{ width: "100%", height: 240 }}>
                <ResponsiveContainer>
                  <BarChart data={(earnings.by_month || []).map((m) => ({ mese: fmtMonth(m.month), incasso: m.revenue }))} margin={{ top: 6, right: 8, left: -12, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff12" vertical={false} />
                    <XAxis dataKey="mese" stroke="#8b95a5" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="#8b95a5" fontSize={11} tickLine={false} axisLine={false} width={44} />
                    <Tooltip cursor={{ fill: "#ffffff08" }} contentStyle={{ background: "#12161f", border: "1px solid #ffffff20", borderRadius: 4, fontSize: 12 }} labelStyle={{ color: "#fff" }} formatter={(v) => [`€ ${Number(v).toFixed(2)}`, "Incasso"]} />
                    <Bar dataKey="incasso" fill="#FFE600" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <h3 className="font-display font-bold text-sm uppercase tracking-wider text-muted-foreground mb-3">Classifica clienti per spesa</h3>
            {(!earnings.clients || earnings.clients.length === 0) ? (
              <div data-testid="earnings-empty" className="flex flex-col items-center py-16 text-muted-foreground">
                <Wallet className="w-12 h-12 mb-3 opacity-40" /><p className="font-light">Nessun guadagno registrato. Aggiungi un prezzo agli ordini.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {earnings.clients.map((c, i) => (
                  <div key={i} data-testid={`earnings-client-${i}`} className="flex items-center justify-between gap-4 p-4 bg-card border border-white/10 rounded-sm">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="shrink-0 w-8 h-8 flex items-center justify-center rounded-sm bg-neon-yellow/10 border border-neon-yellow/30 font-display font-bold text-sm" style={{ color: "#FFE600" }}>{i + 1}</span>
                      <div className="min-w-0">
                        <p className="font-display font-bold text-white truncate">{c.name || "Cliente"}</p>
                        <p className="font-mono text-xs text-muted-foreground truncate">{c.email || "—"} · {c.order_count} ordini</p>
                      </div>
                    </div>
                    <span className="shrink-0 font-display font-black text-lg flex items-center gap-0.5" style={{ color: "#39FF14" }}><Euro className="w-4 h-4" />{(c.total_spent || 0).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}

            <h3 className="font-display font-bold text-sm uppercase tracking-wider text-muted-foreground mt-8 mb-3">Dettaglio incassi per richiesta</h3>
            {(!earnings.orders || earnings.orders.length === 0) ? (
              <div data-testid="earnings-orders-empty" className="flex flex-col items-center py-10 text-muted-foreground">
                <Wallet className="w-10 h-10 mb-3 opacity-40" /><p className="font-light">Nessuna richiesta con incasso.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {earnings.orders.map((o, i) => (
                  <div key={i} data-testid={`earnings-order-${i}`} className="flex items-center justify-between gap-4 p-3.5 bg-card border border-white/10 rounded-sm">
                    <div className="min-w-0">
                      <p className="font-display font-bold text-white truncate">{o.title} <span className="font-mono text-xs" style={{ color: "#FFE600" }}>#{o.order_code}</span></p>
                      <p className="font-mono text-xs text-muted-foreground truncate">{o.client_name} · {o.status}</p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded-sm border ${o.paid ? "text-neon-green border-neon-green/40 bg-neon-green/10" : "text-muted-foreground border-white/15"}`}>{o.paid ? "Pagato" : "Da pagare"}</span>
                      <span className="font-display font-black text-lg flex items-center gap-0.5" style={{ color: (o.price || 0) > 0 ? "#39FF14" : "#8B949E" }}><Euro className="w-4 h-4" />{(o.price || 0).toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* SETTINGS */}
          <TabsContent value="clients" className="mt-6">
            {clients.length === 0 ? (
              <div data-testid="clients-empty" className="flex flex-col items-center py-20 text-muted-foreground">
                <Users className="w-12 h-12 mb-3 opacity-40" /><p className="font-light">Nessun cliente con email registrata.</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {clients.map((c, i) => (
                  <div key={i} data-testid={`client-card-${i}`} className="p-5 bg-card border border-white/10 rounded-sm hover:border-neon-cyan/40 transition-colors duration-200">
                    <ClientOrdersDialog client={c} index={i} />
                    <AddDiscountDialog client={c} index={i} onDone={load} />
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="settings">
            <SettingsEditor />
          </TabsContent>

          {/* TRASH */}
          <TabsContent value="trash" className="mt-6">
            {trashCount === 0 ? (
              <div data-testid="trash-empty" className="flex flex-col items-center py-20 text-muted-foreground">
                <Archive className="w-12 h-12 mb-3 opacity-40" /><p className="font-light">Il cestino è vuoto.</p>
              </div>
            ) : (
              <div className="space-y-8">
                <TrashGroup title="Richieste" kind="requests" items={trash.requests} onRestore={restoreItem} onPurge={purgeItem}
                  render={(it) => <><span className="text-white font-semibold">{it.title}</span> <span className="text-muted-foreground">— {it.client_name}{it.order_code ? ` · #${it.order_code}` : ""}</span></>} />
                <TrashGroup title="Galleria" kind="gallery" items={trash.gallery} onRestore={restoreItem} onPurge={purgeItem}
                  render={(it) => (
                    <span className="flex items-center gap-3">
                      {it.image_path && <img src={fileUrl(it.image_path)} alt="" className="w-10 h-10 object-cover rounded-sm border border-white/10" />}
                      <span className="text-white font-semibold">{it.title}</span>
                    </span>
                  )} />
                <TrashGroup title="Recensioni" kind="feedback" items={trash.feedback} onRestore={restoreItem} onPurge={purgeItem}
                  render={(it) => <><span className="text-white font-semibold">{it.name}</span> <span className="text-muted-foreground">— "{it.message}"</span></>} />
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function TrashGroup({ title, kind, items, onRestore, onPurge, render }) {
  if (!items || items.length === 0) return null;
  return (
    <div data-testid={`trash-group-${kind}`}>
      <h3 className="font-display font-bold text-sm uppercase tracking-wider text-muted-foreground mb-3">{title} ({items.length})</h3>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={it.id} data-testid={`trash-${kind}-item-${i}`} className="flex items-center justify-between gap-4 p-4 bg-card border border-white/10 rounded-sm">
            <div className="min-w-0 text-sm font-light truncate">{render(it)}</div>
            <div className="flex items-center gap-2 shrink-0">
              <Button data-testid={`trash-${kind}-restore-${i}`} onClick={() => onRestore(kind, it.id)} size="sm" variant="outline" className="border-neon-green/40 text-neon-green hover:bg-neon-green hover:text-black rounded-sm gap-1.5">
                <RotateCcw className="w-3.5 h-3.5" /> Ripristina
              </Button>
              <Button data-testid={`trash-${kind}-purge-${i}`} onClick={() => onPurge(kind, it.id)} size="icon" variant="ghost" className="text-muted-foreground hover:text-neon-pink hover:bg-neon-pink/10" title="Elimina definitivamente">
                <XCircle className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function ProgressPhotos({ req, onChanged, index }) {
  const [uploading, setUploading] = useState(false);
  const photos = req.progress_photos || [];
  const onUpload = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/requests/${req.id}/photos`, { image_path: up.data.path });
      toast.success("Foto avanzamento aggiunta");
      onChanged();
    } catch { toast.error("Errore upload"); } finally { setUploading(false); }
  };
  const remove = async (p) => { await api.delete(`/requests/${req.id}/photos`, { params: { path: p } }); toast.success("Foto rimossa"); onChanged(); };
  return (
    <div className="mt-3">
      <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Foto avanzamento (visibili al cliente col codice)</p>
      <div className="flex items-center gap-2 flex-wrap">
        {photos.map((p, k) => (
          <div key={k} className="relative">
            <img src={fileUrl(p)} alt="" className="w-14 h-14 object-cover rounded-sm border border-white/10" />
            <button data-testid={`progress-del-${index}-${k}`} onClick={() => remove(p)} className="absolute -top-1.5 -right-1.5 bg-black/85 text-neon-pink rounded-full p-0.5 border border-white/10"><Trash2 className="w-3 h-3" /></button>
          </div>
        ))}
        <label data-testid={`progress-upload-${index}`} className="w-14 h-14 flex items-center justify-center border border-dashed border-white/20 rounded-sm cursor-pointer hover:border-neon-cyan text-muted-foreground hover:text-neon-cyan transition-colors duration-200">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
          <input type="file" accept="image/*" className="hidden" onChange={onUpload} />
        </label>
      </div>
    </div>
  );
}

function StlDialog({ url, index }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`request-stl-${index}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 h-auto text-xs font-semibold rounded-sm bg-neon-cyan/15 text-neon-cyan border border-neon-cyan/40 hover:bg-neon-cyan hover:text-black transition-colors duration-200">
          <Box className="w-3.5 h-3.5" /> Anteprima 3D
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-white/10 text-white max-w-2xl">
        <DialogHeader><DialogTitle className="font-display tracking-tight">Anteprima modello STL</DialogTitle></DialogHeader>
        {open && <StlViewer url={url} />}
      </DialogContent>
    </Dialog>
  );
}

function EditGalleryDialog({ item, onSaved, index }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(item.title);
  const [description, setDescription] = useState(item.description || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api.put(`/gallery/${item.id}`, { title, description }); toast.success("Foto aggiornata"); setOpen(false); onSaved(); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`gallery-edit-${index}`} size="icon" className="w-8 h-8 bg-black/70 text-neon-cyan hover:bg-neon-cyan hover:text-black rounded-sm"><Pencil className="w-4 h-4" /></Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-white/10 text-white">
        <DialogHeader><DialogTitle className="font-display tracking-tight">Modifica foto</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Input data-testid={`gallery-edit-title-${index}`} value={title} onChange={(e) => setTitle(e.target.value)} className="bg-input border-white/10 text-white" />
          <Textarea data-testid={`gallery-edit-desc-${index}`} rows={4} placeholder="Descrizione del progetto…" value={description} onChange={(e) => setDescription(e.target.value)} className="bg-input border-white/10 text-white resize-none" />
        </div>
        <DialogFooter>
          <Button data-testid={`gallery-edit-save-${index}`} onClick={save} disabled={saving} className="bg-neon-cyan text-black hover:bg-white rounded-sm font-semibold">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function NewRequestDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ client_name: "", service_type: "Stampa 3D", title: "", description: "", material: "", quantity: 1 });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    if (!form.client_name || !form.title) { toast.error("Nome e titolo obbligatori"); return; }
    setSaving(true);
    try {
      await api.post("/requests", { ...form, quantity: Number(form.quantity) || 1, privacy_consent: true });
      toast.success("Richiesta creata");
      setForm({ client_name: "", service_type: "Stampa 3D", title: "", description: "", material: "", quantity: 1 });
      setOpen(false); onCreated();
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-request-open" className="bg-neon-cyan text-black hover:bg-white rounded-sm font-semibold gap-2 transition-colors duration-200"><Plus className="w-4 h-4" /> Nuova richiesta</Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-white/10 text-white">
        <DialogHeader><DialogTitle className="font-display tracking-tight">Nuova richiesta</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Input data-testid="admin-req-name" placeholder="Nome cliente *" value={form.client_name} onChange={set("client_name")} className="bg-input border-white/10 text-white" />
          <Select value={form.service_type} onValueChange={(v) => setForm({ ...form, service_type: v })}>
            <SelectTrigger data-testid="admin-req-service" className="bg-input border-white/10 text-white rounded-sm"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-card border-white/10 text-white">
              {SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input data-testid="admin-req-title" placeholder="Titolo progetto *" value={form.title} onChange={set("title")} className="bg-input border-white/10 text-white" />
          <Textarea data-testid="admin-req-desc" placeholder="Descrizione" rows={3} value={form.description} onChange={set("description")} className="bg-input border-white/10 text-white resize-none" />
          <div className="grid grid-cols-2 gap-4">
            <Input data-testid="admin-req-material" placeholder="Materiale" value={form.material} onChange={set("material")} className="bg-input border-white/10 text-white" />
            <Input data-testid="admin-req-qty" type="number" min="1" placeholder="Quantità" value={form.quantity} onChange={set("quantity")} className="bg-input border-white/10 text-white" />
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="admin-req-save" onClick={save} disabled={saving} className="bg-neon-cyan text-black hover:bg-white rounded-sm font-semibold">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crea"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const waMessage = (r, photoUrl, amount) => {
  let msg;
  if (r.status === "Consegnato") {
    const reviewUrl = `${window.location.origin}/recensioni`;
    msg = `Ciao ${r.client_name}! 🎉 Il tuo ordine ${r.order_code || ""} (${r.title}) è stato consegnato. Grazie per aver scelto AC Creator 3D! Ci faresti un enorme piacere lasciando una recensione ⭐\n\n👉 ${reviewUrl}`;
  } else if (r.status === "In colorazione") {
    msg = `Ciao ${r.client_name}! 🎨 Il tuo ordine ${r.order_code || ""} (${r.title}) è entrato nella fase di colorazione. Stiamo rifinendo colori e dettagli, ti avvisiamo appena è pronto! — AC Creator 3D`;
  } else {
    msg = `Ciao ${r.client_name}, aggiornamento sul tuo ordine ${r.order_code || ""} (${r.title}): stato attuale "${r.status}". — AC Creator 3D`;
  }
  if (amount) msg += `\n\n💶 Importo: €${amount}`;
  if (photoUrl) msg += `\n\n📷 Guarda la foto del tuo lavoro: ${photoUrl}`;
  return msg;
};
const waHref = (r, photoUrl, amount) => {
  const phone = (r.client_phone || "").replace(/[^0-9]/g, "");
  return `https://api.whatsapp.com/send?phone=${phone}&text=${encodeURIComponent(waMessage(r, photoUrl, amount))}`;
};

function WhatsAppDialog({ r, index, onChanged }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [amount, setAmount] = useState("");
  const [uploading, setUploading] = useState(false);
  const photos = r.progress_photos || [];
  const uploadNew = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/requests/${r.id}/photos`, { image_path: up.data.path });
      setSelected(up.data.path);
      toast.success("Foto caricata e aggiunta all'ordine");
      onChanged && onChanged();
    } catch { toast.error("Errore nel caricamento della foto"); }
    finally { setUploading(false); }
  };
  const openWhatsApp = () => {
    window.open(waHref(r, selected ? fileUrl(selected) : "", amount.trim()), "_blank", "noopener");
    setOpen(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`request-whatsapp-${index}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-sm bg-[#25D366]/15 text-[#25D366] border border-[#25D366]/40 hover:bg-[#25D366] hover:text-black transition-colors duration-200">
          <MessageCircle className="w-3.5 h-3.5" /> Scrivi su WhatsApp
        </button>
      </DialogTrigger>
      <DialogContent className="bg-card border-white/10 text-white">
        <DialogHeader><DialogTitle className="font-display tracking-tight">WhatsApp — {r.status}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-xs text-muted-foreground font-light">Puoi allegare una foto: nel messaggio verrà inserito il link alla foto (WhatsApp non permette di allegarla in automatico dal web). Scegline una già caricata o caricane una nuova.</p>
          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2 block">Importo (facoltativo)</label>
            <div className="flex items-center gap-2">
              <Euro className="w-4 h-4 text-neon-cyan shrink-0" />
              <Input data-testid={`wa-amount-${index}`} type="number" min="0" step="0.01" placeholder="Es. 25" value={amount} onChange={(e) => setAmount(e.target.value)} className="bg-input border-white/10 text-white" />
            </div>
          </div>
          {photos.length > 0 && (
            <div>
              <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2">Foto dell'ordine</p>
              <div className="flex gap-2 flex-wrap">
                {photos.map((p, k) => (
                  <button key={k} data-testid={`wa-photo-${index}-${k}`} onClick={() => setSelected(selected === p ? null : p)}
                    className={`relative w-16 h-16 rounded-sm overflow-hidden border-2 transition-colors duration-200 ${selected === p ? "border-neon-green" : "border-white/10 hover:border-white/40"}`}>
                    <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                    {selected === p && <span className="absolute inset-0 bg-neon-green/25 flex items-center justify-center"><Check className="w-5 h-5 text-neon-green" /></span>}
                  </button>
                ))}
              </div>
            </div>
          )}
          <label data-testid={`wa-upload-${index}`} className="flex items-center gap-3 cursor-pointer border border-dashed border-white/20 hover:border-neon-cyan/60 rounded-sm px-4 py-3 transition-colors duration-200">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin text-neon-cyan" /> : <UploadCloud className="w-4 h-4 text-neon-cyan" />}
            <span className="text-sm text-muted-foreground">{uploading ? "Caricamento…" : "Carica una nuova foto"}</span>
            <input type="file" accept="image/*" className="hidden" onChange={uploadNew} disabled={uploading} />
          </label>
          <div className="flex items-center gap-2 text-xs font-mono" style={{ color: selected ? "#39FF14" : "#8B949E" }}>
            <ImageIcon className="w-4 h-4" /> {selected ? "Foto selezionata — link incluso nel messaggio" : "Nessuna foto (messaggio solo testo)"}
          </div>
        </div>
        <DialogFooter>
          <Button data-testid={`wa-open-${index}`} onClick={openWhatsApp} className="bg-[#25D366] text-black hover:bg-white rounded-sm font-semibold gap-2">
            <MessageCircle className="w-4 h-4" /> Apri WhatsApp
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ClientOrdersDialog({ client, index }) {
  const [open, setOpen] = useState(false);
  const [orders, setOrders] = useState(null);
  const fetchOrders = async () => {
    setOrders(null);
    try {
      const r = await api.get("/clients/orders", { params: { email: client.email || "", name: client.name || "" } });
      setOrders(r.data);
    } catch { setOrders([]); }
  };
  const total = (orders || []).reduce((s, o) => s + (o.price || 0), 0);
  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) fetchOrders(); }}>
      <DialogTrigger asChild>
        <button data-testid={`client-open-${index}`} className="w-full text-left cursor-pointer">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-display font-bold text-white truncate">{client.name}</h3>
            {client.loyalty_eligible && (
              <span data-testid={`client-loyalty-${index}`} className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase rounded-sm bg-neon-green/15 text-neon-green border border-neon-green/40"><Gift className="w-3 h-3" /> Fedele</span>
            )}
          </div>
          <p className="text-xs font-mono text-muted-foreground mt-1 truncate">{client.email || "—"}</p>
          {client.phone && <p className="text-xs font-mono text-muted-foreground truncate">{client.phone}</p>}
          <div className="grid grid-cols-2 gap-2 mt-4">
            <div className="border border-white/10 rounded-sm p-3 text-center">
              <p className="font-display font-black text-2xl text-neon-cyan">{client.order_count}</p>
              <p className="text-[10px] uppercase font-mono text-muted-foreground">Ordini</p>
            </div>
            <div className="border border-white/10 rounded-sm p-3 text-center">
              <p className="font-display font-black text-2xl text-neon-green flex items-center justify-center gap-0.5"><Euro className="w-4 h-4" />{(client.total_spent || 0).toFixed(2)}</p>
              <p className="text-[10px] uppercase font-mono text-muted-foreground">Speso</p>
            </div>
          </div>
          <div className="mt-3 h-1.5 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full bg-neon-green" style={{ width: `${Math.min(client.order_count, 10) * 10}%` }} />
          </div>
          <div className="mt-2 flex items-center justify-between text-xs font-mono">
            <span className="text-muted-foreground">Fedeltà {Math.min(client.order_count, 10)}/10</span>
            {client.discount_code && <span data-testid={`client-code-${index}`} className="text-neon-green">{client.discount_code}</span>}
          </div>
          <p className="mt-3 text-xs font-mono text-neon-cyan flex items-center gap-1">Vedi ordini dettagliati →</p>
        </button>
      </DialogTrigger>
      <DialogContent className="bg-card border-white/10 text-white sm:max-w-lg">
        <DialogHeader><DialogTitle className="font-display tracking-tight">Ordini di {client.name}</DialogTitle></DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto space-y-2 pr-1">
          {orders === null ? (
            <div className="flex items-center gap-2 text-muted-foreground py-8 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento…</div>
          ) : orders.length === 0 ? (
            <p className="text-muted-foreground font-light py-8 text-center">Nessun ordine per questo cliente.</p>
          ) : orders.map((o, k) => (
            <div key={k} data-testid={`client-order-${index}-${k}`} className="flex items-center justify-between gap-3 p-3 border border-white/10 rounded-sm">
              <div className="min-w-0">
                <p className="font-display font-bold text-white truncate">{o.title} <span className="font-mono text-xs" style={{ color: "#FFE600" }}>#{o.order_code}</span></p>
                <p className="font-mono text-[11px] text-muted-foreground truncate">{o.status}{o.created_at ? ` · ${new Date(o.created_at).toLocaleDateString("it-IT")}` : ""}</p>
              </div>
              <span className="shrink-0 flex items-center gap-2">
                <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded-sm border ${o.paid ? "text-neon-green border-neon-green/40 bg-neon-green/10" : "text-muted-foreground border-white/15"}`}>{o.paid ? "Pagato" : "Da pagare"}</span>
                <span className="font-display font-black flex items-center gap-0.5" style={{ color: (o.price || 0) > 0 ? "#39FF14" : "#8B949E" }}><Euro className="w-4 h-4" />{(o.price || 0).toFixed(2)}</span>
              </span>
            </div>
          ))}
        </div>
        {orders && orders.length > 0 && (
          <div className="border-t border-white/10 pt-3 flex items-center justify-between">
            <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Totale pagato</span>
            <span data-testid={`client-orders-total-${index}`} className="font-display font-black text-lg flex items-center gap-0.5" style={{ color: "#39FF14" }}><Euro className="w-4 h-4" />{total.toFixed(2)}</span>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AddDiscountDialog({ client, index, onDone }) {
  const [open, setOpen] = useState(false);
  const [percent, setPercent] = useState(10);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const presets = [5, 10, 15, 20];
  const save = async () => {
    const p = Number(percent);
    if (!p || p < 1 || p > 100) { toast.error("Percentuale tra 1 e 100"); return; }
    setSaving(true);
    try {
      const r = await api.post("/clients/discount", { email: client.email, name: client.name, percent: p });
      setResult(r.data);
      toast.success(`Sconto ${p}% creato`);
      onDone();
    } catch { toast.error("Errore nella creazione dello sconto"); }
    finally { setSaving(false); }
  };
  const reset = (v) => { setOpen(v); if (!v) { setResult(null); setPercent(10); } };
  const copyCode = () => { if (result?.code) { navigator.clipboard?.writeText(result.code); toast.success("Codice copiato"); } };
  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogTrigger asChild>
        <Button data-testid={`client-add-discount-${index}`} variant="outline" size="sm"
          className="mt-3 w-full border-neon-green/40 text-neon-green hover:bg-neon-green hover:text-black rounded-sm font-semibold gap-2 transition-colors duration-200">
          <Gift className="w-4 h-4" /> Aggiungi sconto fedeltà
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-white/10 text-white">
        <DialogHeader><DialogTitle className="font-display tracking-tight">Sconto fedeltà — {client.name}</DialogTitle></DialogHeader>
        {result ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground font-light">Buono sconto del <b className="text-neon-green">{result.percent}%</b> creato per <span className="font-mono">{client.email}</span>. Inviato via email al cliente.</p>
            <div className="flex items-center gap-2 p-4 rounded-sm border border-neon-green/40 bg-neon-green/5">
              <span data-testid="discount-result-code" className="font-mono text-xl font-bold text-neon-green tracking-widest flex-1">{result.code}</span>
              <Button data-testid="discount-copy-btn" onClick={copyCode} size="icon" className="w-9 h-9 bg-black/50 text-neon-green hover:bg-neon-green hover:text-black rounded-sm"><Copy className="w-4 h-4" /></Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground font-light">Scegli la percentuale di sconto da regalare a questo cliente. Verrà generato un codice e inviato via email.</p>
            <div className="flex gap-2">
              {presets.map((p) => (
                <button key={p} data-testid={`discount-preset-${p}`} onClick={() => setPercent(p)}
                  className={`flex-1 py-2 rounded-sm border font-mono text-sm transition-colors duration-200 ${Number(percent) === p ? "border-neon-green bg-neon-green/15 text-neon-green" : "border-white/15 text-muted-foreground hover:border-white/40"}`}>
                  {p}%
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Percent className="w-4 h-4 text-neon-green shrink-0" />
              <Input data-testid="discount-percent-input" type="number" min="1" max="100" value={percent}
                onChange={(e) => setPercent(e.target.value)} className="bg-input border-white/10 text-white" />
            </div>
          </div>
        )}
        <DialogFooter>
          {result ? (
            <Button data-testid="discount-done-btn" onClick={() => reset(false)} className="bg-neon-cyan text-black hover:bg-white rounded-sm font-semibold">Chiudi</Button>
          ) : (
            <Button data-testid="discount-create-btn" onClick={save} disabled={saving} className="bg-neon-green text-black hover:bg-white rounded-sm font-semibold">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crea sconto"}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewFolderDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!name.trim()) { toast.error("Nome cartella obbligatorio"); return; }
    setSaving(true);
    try { await api.post("/gallery/folders", { name: name.trim() }); toast.success("Cartella creata"); setName(""); setOpen(false); onCreated(); }
    catch { toast.error("Errore creazione cartella"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-folder-open" variant="outline" className="border-white/15 text-white hover:bg-white/5 rounded-sm font-semibold gap-2"><FolderPlus className="w-4 h-4" /> Nuova cartella</Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-white/10 text-white">
        <DialogHeader><DialogTitle className="font-display tracking-tight">Nuova cartella</DialogTitle></DialogHeader>
        <Input data-testid="folder-name-input" placeholder="Nome cartella *" value={name} onChange={(e) => setName(e.target.value)} className="bg-input border-white/10 text-white" />
        <DialogFooter>
          <Button data-testid="folder-create-save" onClick={save} disabled={saving} className="bg-neon-cyan text-black hover:bg-white rounded-sm font-semibold">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crea"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RenameFolderDialog({ folder, onSaved }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(folder.name);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!name.trim()) { toast.error("Nome cartella obbligatorio"); return; }
    setSaving(true);
    try { await api.put(`/gallery/folders/${folder.id}`, { name: name.trim() }); toast.success("Cartella rinominata"); setOpen(false); onSaved(); }
    catch { toast.error("Errore rinomina"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`folder-rename-${folder.id}`} size="icon" className="w-7 h-7 bg-black/50 text-neon-cyan hover:bg-neon-cyan hover:text-black rounded-sm"><Pencil className="w-3.5 h-3.5" /></Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-white/10 text-white">
        <DialogHeader><DialogTitle className="font-display tracking-tight">Rinomina cartella</DialogTitle></DialogHeader>
        <Input data-testid="folder-rename-input" value={name} onChange={(e) => setName(e.target.value)} className="bg-input border-white/10 text-white" />
        <DialogFooter>
          <Button data-testid="folder-rename-save" onClick={save} disabled={saving} className="bg-neon-cyan text-black hover:bg-white rounded-sm font-semibold">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MoveDialog({ open, onOpenChange, photoIds, folders, onMoved }) {
  const [target, setTarget] = useState("");
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);
  const move = async () => {
    if (!target) { toast.error("Scegli una destinazione"); return; }
    setSaving(true);
    try {
      let folderId = target === "__none__" ? null : target;
      if (target === "__new__") {
        if (!newName.trim()) { toast.error("Nome cartella obbligatorio"); setSaving(false); return; }
        const r = await api.post("/gallery/folders", { name: newName.trim() });
        folderId = r.data.id;
      }
      await api.post("/gallery/move", { photo_ids: photoIds, folder_id: folderId });
      toast.success(photoIds.length === 1 ? "Foto spostata" : `${photoIds.length} foto spostate`);
      onOpenChange(false); setTarget(""); setNewName(""); onMoved();
    } catch { toast.error("Errore spostamento"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-white/10 text-white">
        <DialogHeader><DialogTitle className="font-display tracking-tight">Sposta {photoIds.length} foto</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Select value={target} onValueChange={setTarget}>
            <SelectTrigger data-testid="move-target-select" className="bg-input border-white/10 text-white rounded-sm"><SelectValue placeholder="Scegli cartella di destinazione…" /></SelectTrigger>
            <SelectContent className="bg-card border-white/10 text-white">
              <SelectItem value="__none__">Senza cartella</SelectItem>
              <SelectItem value="__new__">➕ Nuova cartella…</SelectItem>
              {folders.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {target === "__new__" && (
            <Input data-testid="move-new-folder-name" placeholder="Nome nuova cartella *" value={newName} onChange={(e) => setNewName(e.target.value)} className="bg-input border-white/10 text-white" />
          )}
        </div>
        <DialogFooter>
          <Button data-testid="move-confirm" onClick={move} disabled={saving} className="bg-neon-cyan text-black hover:bg-white rounded-sm font-semibold">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sposta"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PhotoCard({ g, index, selected, onToggle, onQuickMove, onSaved, onDelete }) {
  return (
    <div data-testid={`admin-gallery-item-${index}`} className={`group relative overflow-hidden rounded-sm border bg-card transition-colors duration-200 ${selected ? "border-neon-cyan" : "border-white/10"}`}>
      <button data-testid={`gallery-select-${index}`} onClick={() => onToggle(g.id)}
        className={`absolute top-2 left-2 z-10 w-6 h-6 rounded-sm border flex items-center justify-center transition-colors duration-200 ${selected ? "bg-neon-cyan border-neon-cyan text-black" : "bg-black/60 border-white/30 text-transparent hover:border-white"}`}>
        <Check className="w-4 h-4" />
      </button>
      <img src={fileUrl(g.image_path)} alt={g.title} className="w-full h-44 object-cover" loading="lazy" />
      <div className="p-3">
        <h4 className="font-display font-bold text-sm text-white truncate">{g.title}</h4>
        <p className="text-xs text-muted-foreground font-light mt-1 line-clamp-2">{g.description || "Nessuna descrizione"}</p>
      </div>
      <div className="absolute top-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
        <Button data-testid={`gallery-move-${index}`} onClick={() => onQuickMove(g.id)} size="icon" className="w-8 h-8 bg-black/70 text-white hover:bg-neon-cyan hover:text-black rounded-sm"><FolderInput className="w-4 h-4" /></Button>
        <EditGalleryDialog item={g} onSaved={onSaved} index={index} />
        <Button data-testid={`gallery-delete-${index}`} onClick={() => onDelete(g.id)} size="icon" className="w-8 h-8 bg-black/70 text-neon-pink hover:bg-neon-pink hover:text-white rounded-sm"><Trash2 className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}

function NewGalleryDialog({ folders = [], onCreated }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [folderChoice, setFolderChoice] = useState("__none__");
  const [newFolderName, setNewFolderName] = useState("");
  const save = async () => {
    if (!title || files.length === 0) { toast.error("Titolo e almeno un'immagine obbligatori"); return; }
    let folderId = folderChoice === "__none__" ? null : folderChoice;
    if (folderChoice === "__new__") {
      if (!newFolderName.trim()) { toast.error("Nome cartella obbligatorio"); return; }
      try { const r = await api.post("/gallery/folders", { name: newFolderName.trim() }); folderId = r.data.id; }
      catch { toast.error("Errore creazione cartella"); return; }
    }
    setSaving(true);
    const total = files.length;
    setProgress({ done: 0, total });
    let done = 0;
    const failed = [];
    const uploadOne = async (f) => {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        await api.post("/gallery", { title, description, image_path: up.data.path, folder_id: folderId });
      } catch { failed.push(f); }
      finally { done += 1; setProgress({ done, total }); }
    };
    const queue = [...files];
    const workers = Array.from({ length: Math.min(3, queue.length) }, async () => {
      while (queue.length) { const f = queue.shift(); await uploadOne(f); }
    });
    try {
      await Promise.all(workers);
      const ok = total - failed.length;
      if (ok > 0) onCreated();
      if (failed.length === 0) {
        toast.success(ok === 1 ? "Foto aggiunta" : `${ok} foto aggiunte`);
        setTitle(""); setDescription(""); setFiles([]); setFolderChoice("__none__"); setNewFolderName(""); setOpen(false);
      } else {
        toast.error(`Caricate ${ok}/${total} foto. Riprova per le ${failed.length} rimaste.`);
        setFiles(failed);
      }
    } finally { setSaving(false); setProgress({ done: 0, total: 0 }); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-gallery-open" className="bg-neon-pink text-white hover:bg-white hover:text-black rounded-sm font-semibold gap-2 transition-colors duration-200"><Plus className="w-4 h-4" /> Aggiungi foto</Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-white/10 text-white">
        <DialogHeader><DialogTitle className="font-display tracking-tight">Nuove foto in galleria</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Input data-testid="gallery-title" placeholder="Titolo *" value={title} onChange={(e) => setTitle(e.target.value)} className="bg-input border-white/10 text-white" />
          <Textarea data-testid="gallery-desc" placeholder="Descrizione" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} className="bg-input border-white/10 text-white resize-none" />
          <div>
            <Label className="text-xs text-muted-foreground mb-1.5 block">Cartella</Label>
            <Select value={folderChoice} onValueChange={setFolderChoice}>
              <SelectTrigger data-testid="gallery-folder-select" className="bg-input border-white/10 text-white rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-card border-white/10 text-white">
                <SelectItem value="__none__">Senza cartella</SelectItem>
                <SelectItem value="__new__">➕ Nuova cartella…</SelectItem>
                {folders.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {folderChoice === "__new__" && (
            <Input data-testid="gallery-new-folder-name" placeholder="Nome nuova cartella *" value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} className="bg-input border-white/10 text-white" />
          )}
          <label data-testid="gallery-file-label" className="flex items-center gap-3 cursor-pointer border border-dashed border-white/20 hover:border-neon-pink/60 rounded-sm px-4 py-4 transition-colors duration-200">
            <UploadCloud className="w-5 h-5 text-neon-pink shrink-0" />
            <span className="text-sm text-muted-foreground truncate">{files.length > 0 ? (files.length === 1 ? files[0].name : `${files.length} immagini selezionate`) : "Carica una o più immagini *"}</span>
            <input data-testid="gallery-file-input" type="file" accept="image/*" multiple className="hidden" onChange={(e) => setFiles(Array.from(e.target.files))} />
          </label>
          <p className="text-xs font-mono text-muted-foreground">Puoi selezionare più foto: verranno raccolte nella stessa cartella.</p>
          {saving && progress.total > 0 && (
            <div data-testid="gallery-upload-progress" className="space-y-1.5">
              <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full bg-neon-pink transition-all duration-200" style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }} />
              </div>
              <p className="text-xs font-mono text-neon-pink">Caricamento {progress.done}/{progress.total}…</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button data-testid="gallery-save" onClick={save} disabled={saving} className="bg-neon-pink text-white hover:bg-white hover:text-black rounded-sm font-semibold">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> {progress.total > 1 ? `Caricamento ${progress.done}/${progress.total}` : "Caricamento…"}</> : "Aggiungi"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
